/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ComponentProps, ComponentPropsWithRef, ComponentType, FC } from 'react';
import { HOC } from './types';

export class Base<TAccumulatedProps = object> {
  private name: string;
  private hocs: HOC<any>[] = [];

  constructor(name: string) {
    this.name = name;
  }

  /**
   * Creates a typed HOC from a factory function that accepts arguments.
   * It infers the injected props from the return type of the HOC and accumulates them.
   *
   * @param hocWithArgs - A function that returns an HOC when called with a component and arguments.
   * @param args - Arguments to pass to the HOC factory.
   * @returns A typed HOC that can be added to the builder pipeline.
   */
  composeWithArgsFactory<
    H extends <C extends ComponentType<any>>(component: C, ...a: any[]) => ComponentType<any>,
    A extends any[],
    ReturnedProps = H extends <C extends ComponentType<any>>(c: C, ...a: any[]) => ComponentType<infer P> ? P : never,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    InferredInjected = ReturnedProps extends ComponentPropsWithRef<infer _C> & infer I
      ? I
      : Omit<ReturnedProps, keyof ComponentPropsWithRef<ComponentType<any>>>,
  >(hocWithArgs: H, ...args: A) {
    const hoc: HOC<TAccumulatedProps & (InferredInjected extends object ? InferredInjected : unknown)> = <C extends ComponentType<any>>(
      Component: C,
    ) => {
      const Wrapped = (hocWithArgs as any)(Component, ...args) as ComponentType<any>;
      return Wrapped as ComponentType<any>;
    };

    return hoc;
  }

  /**
   * Composes all registered HOCs and returns a new component that accepts both
   * the original props and all accumulated injected props.
   *
   * @param Component - The base React component to enhance.
   * @returns A new component with merged props and a descriptive displayName.
   */
  build<C extends ComponentType<any>>(Component: C): ComponentType<ComponentProps<C> & TAccumulatedProps> {
    const Enhanced = this.hocs.reduceRight<ComponentType<any>>((acc, hoc) => hoc(acc), Component);
    const Wrapped: FC<ComponentProps<C> & TAccumulatedProps> = (props) => {
      return <Enhanced {...(props as any)} />;
    };
    Wrapped.displayName = `${this.name}(${Component.displayName || Component.name || 'Component'})`;
    return Wrapped;
  }

  /**
   * Builds a component with some props pre-injected. The returned component only
   * requires the remaining props.
   *
   * @param Component - The base component to enhance.
   * @param injectedProps - A subset of props to inject automatically.
   * @returns A component with the remaining props required, and injected ones optional.
   */
  buildWithProps<C extends ComponentType<any>, K extends keyof (ComponentProps<C> & TAccumulatedProps)>(
    Component: C,
    injectedProps: Pick<ComponentProps<C> & TAccumulatedProps, K>,
  ) {
    const Enhanced = this.hocs.reduceRight<ComponentType<any>>((acc, hoc) => hoc(acc), Component);

    type FullProps = ComponentProps<C> & TAccumulatedProps;
    type Remaining = Omit<FullProps, K>;

    const Wrapped: FC<Remaining> = (props) => {
      const allProps = { ...(injectedProps as object), ...(props as object) } as FullProps;
      return <Enhanced {...allProps} />;
    };

    Wrapped.displayName = `${this.name}(${Component.displayName || Component.name || 'Component'})`;
    return Wrapped as ComponentType<Omit<FullProps, K> & Partial<Pick<FullProps, K>>>;
  }

  /**
   * Sets the internal list of HOCs to apply during build.
   */
  set setHocs(hocs: HOC<any>[]) {
    this.hocs = hocs;
  }

  /**
   * Retrieves the current list of HOCs.
   */
  get getHocs() {
    return this.hocs;
  }
}
